(() => {
  'use strict';

  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');

  const SELECTORS = {
    root: 'carousel-images-component.carousel-images-component',
    track: '.carousel-images__items',
    item: '.carousel-images-item',
    prev: '.carousel-images-component__button--prev',
    next: '.carousel-images-component__button--next'
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  class MobileCarouselImages {
    constructor(root) {
      this.root = root;
      this.isDesktopCarousel = root.dataset.desktopCarousel === 'true';
      this.track = root.querySelector(SELECTORS.track);
      this.items = Array.from(root.querySelectorAll(SELECTORS.item));
      this.btnPrev = root.querySelector(SELECTORS.prev);
      this.btnNext = root.querySelector(SELECTORS.next);

      if (!this.track || !this.items.length || !this.btnPrev || !this.btnNext) return;

      this.isDown = false;
      this.dragMoved = false;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.raf = null;

      this.scrollEndT = null;

      this.itemCenters = [];
      this.maxScrollLeft = 0;

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.attach();
      this.refresh();
      this.updateButtons();
    }

    attach() {
      this.btnPrev.addEventListener('click', this.onPrev);
      this.btnNext.addEventListener('click', this.onNext);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });

      this.root.setAttribute('role', 'region');
      this.root.setAttribute('aria-label', this.root.getAttribute('aria-label') || 'Image carousel');
      this.track.setAttribute('role', 'list');
      this.items.forEach((li, i) => li.setAttribute('aria-label', `Slide ${i + 1} of ${this.items.length}`));
    }

    detach() {
      this.btnPrev.removeEventListener('click', this.onPrev);
      this.btnNext.removeEventListener('click', this.onNext);
      this.track.removeEventListener('scroll', this.onScroll);

      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);

      clearTimeout(this.scrollEndT);
      this.scrollEndT = null;

      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    isActive() {
      return MOBILE_MQ.matches || this.isDesktopCarousel;
    }

    onResize() {
      if (!this.isActive()) return;
      this.refresh();
      if (!this.isDesktopCarousel) this.snapToNearest({ smooth: false });
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateButtons();
        this.updateAriaCurrent();
      });

      clearTimeout(this.scrollEndT);
      this.scrollEndT = setTimeout(() => {
        if (!this.isActive() || this.isDesktopCarousel) return;
        this.snapToNearest({ smooth: true });
      }, 120);
    }

    refresh() {
      const trackRect = this.track.getBoundingClientRect();
      const trackLeft = trackRect.left;

      this.itemCenters = this.items.map((li) => {
        const r = li.getBoundingClientRect();
        const centerInViewport = (r.left + r.width / 2);
        const centerRelativeToTrackViewport = centerInViewport - trackLeft;
        return centerRelativeToTrackViewport;
      });

      this.maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    getNearestIndex() {
      if (this.isDesktopCarousel) {
        const trackRect = this.track.getBoundingClientRect();
        const leftPadding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        const refLeft = trackRect.left + leftPadding;
        let bestIdx = 0, bestDist = Infinity;
        this.items.forEach((item, i) => {
          const d = Math.abs(item.getBoundingClientRect().left - refLeft);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        return bestIdx;
      }

      const viewportCenter = this.track.clientWidth / 2;
      const currentCenter = this.track.scrollLeft + viewportCenter;
      let bestIdx = 0;
      let bestDist = Infinity;

      for (let i = 0; i < this.itemCenters.length; i++) {
        const itemCenterAbs = this.track.scrollLeft + this.itemCenters[i];
        const d = Math.abs(itemCenterAbs - currentCenter);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    }

    scrollToIndex(idx, smooth = true) {
      idx = clamp(idx, 0, this.items.length - 1);

      let target;
      if (this.isDesktopCarousel) {
        // Scroll so item[idx] sits at the same left-edge position as item[0] at rest.
        // getBoundingClientRect scroll terms cancel: target = physical[idx] - physical[0]
        const firstRect = this.items[0].getBoundingClientRect();
        const targetRect = this.items[idx].getBoundingClientRect();
        target = targetRect.left - firstRect.left;
      } else {
        const viewportCenter = this.track.clientWidth / 2;
        target = this.track.scrollLeft + this.itemCenters[idx] - viewportCenter;
      }

      const bounded = clamp(target, 0, this.maxScrollLeft);
      this.track.scrollTo({
        left: bounded,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }

    snapToNearest({ smooth = true } = {}) {
      this.refresh();
      const idx = this.getNearestIndex();
      this.scrollToIndex(idx, smooth);
    }

    updateButtons() {
      const left = this.track.scrollLeft;
      const max = this.maxScrollLeft || Math.max(0, this.track.scrollWidth - this.track.clientWidth);

      const atStart = left <= 2;
      const atEnd = left >= (max - 2);

      this.btnPrev.disabled = atStart;
      this.btnNext.disabled = atEnd;

      this.btnPrev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      this.btnNext.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    updateAriaCurrent() {
      const idx = this.getNearestIndex();
      this.items.forEach((li, i) => {
        if (i === idx) li.setAttribute('aria-current', 'true');
        else li.removeAttribute('aria-current');
      });
    }

    onPrev() {
      this.refresh();
      const idx = this.getNearestIndex();
      this.scrollToIndex(idx - 1, true);
    }

    onNext() {
      this.refresh();
      const idx = this.getNearestIndex();
      this.scrollToIndex(idx + 1, true);
    }

    onPointerDown(e) {
      if (!this.isActive()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this.isDown = true;
      this.dragMoved = false;
      this.startX = e.clientX;
      this.startScrollLeft = this.track.scrollLeft;

      try { this.track.setPointerCapture(e.pointerId); } catch (_) {}
    }

    onPointerMove(e) {
      if (!this.isDown || !this.isActive()) return;

      const dx = e.clientX - this.startX;
      if (Math.abs(dx) > 2) this.dragMoved = true;

      if (e.pointerType === 'mouse') e.preventDefault();

      const next = clamp(this.startScrollLeft - dx, 0, this.maxScrollLeft || (this.track.scrollWidth - this.track.clientWidth));
      this.track.scrollLeft = next;
    }

    onPointerUp(e) {
      if (!this.isDown) return;
      this.isDown = false;

      if (this.dragMoved) {
        this.snapToNearest({ smooth: true });
      }
    }
  }

  const instances = new Map();

  const mountAll = () => {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new MobileCarouselImages(root));
    });
  };

  const unmountAll = () => {
    instances.forEach((inst, root) => {
      if (inst && typeof inst.detach === 'function') inst.detach();
      instances.delete(root);
    });
  };

  const sync = () => {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      const active = MOBILE_MQ.matches || root.dataset.desktopCarousel === 'true';
      if (active) {
        if (!instances.has(root)) instances.set(root, new MobileCarouselImages(root));
      } else {
        const inst = instances.get(root);
        if (inst) {
          if (typeof inst.detach === 'function') inst.detach();
          instances.delete(root);
        }
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      sync();
      MOBILE_MQ.addEventListener?.('change', sync);
    });
  } else {
    sync();
    MOBILE_MQ.addEventListener?.('change', sync);
  }
})();